import type { PerfSnapshot } from './PerfMonitor';

/**
 * Automatic degraded mode.
 *
 * Levels trade inference resolution, render pixel ratio and inference cadence
 * for frame rate. The camera stream itself is never restarted (fragile on
 * iOS); the inference input is downscaled instead, which is where the cost is.
 *
 * Step down after 3 s over budget, step up after 8 s of real margin (A110Y: the
 * old 2 s / 10 s pair with a 30 ms budget was a one-way ratchet on iPhone), to
 * avoid oscillation; thermal throttling on phones makes the budget drift.
 *
 * Start-up (A110W): the app holds the governor while the engine warms up (its
 * first frames and inferences carry one-off costs that say nothing about the
 * device) and arms it once measurements are meaningful. During a short window
 * after arming, a device far over budget (fps under half the minimum, or
 * inference over twice its budget) steps down after ~1 s instead of 2 s, so
 * the first seconds converge quickly instead of stuttering through each level.
 * Outside that window the rules above apply unchanged.
 */
export interface QualityLevel {
  name: 'high' | 'medium' | 'low' | 'minimal';
  /** Long side of the bitmap sent to the tracker. */
  inferLongSide: number;
  /** Cap on devicePixelRatio for the WebGL canvas. */
  pixelRatioCap: number;
  /** Run inference every Nth rendered frame (prediction fills the gaps). */
  inferEveryNthFrame: number;
  /** Shadow map resolution for the frame's shadow on the face (0 = no shadow). */
  shadowMapSize: number;
}

export const QUALITY_LEVELS: QualityLevel[] = [
  { name: 'high', inferLongSide: 960, pixelRatioCap: 2, inferEveryNthFrame: 1, shadowMapSize: 1024 },
  { name: 'medium', inferLongSide: 768, pixelRatioCap: 1.5, inferEveryNthFrame: 1, shadowMapSize: 1024 },
  { name: 'low', inferLongSide: 640, pixelRatioCap: 1.25, inferEveryNthFrame: 1, shadowMapSize: 512 },
  // A110Y: every level infers every frame (the back-pressure already paces the tracker); at 2 the
  // cadence halved and the vibration in live mode doubled.
  { name: 'minimal', inferLongSide: 512, pixelRatioCap: 1, inferEveryNthFrame: 1, shadowMapSize: 0 },
];

export interface GovernorParams {
  /** Inference time above which we step down (ms). */
  inferBudgetMs: number;
  /** Render fps below which we step down. */
  minFps: number;
  stepDownAfterMs: number;
  stepUpAfterMs: number;
  /** Disable automatic changes (debug panel). */
  locked: boolean;
  /** Start-up window after `arm()` (ms) during which a severe overload steps down faster. */
  startupWindowMs: number;
  /** Step-down delay for a severe overload inside the start-up window (ms). */
  severeStepDownAfterMs: number;
  /** Severe overload: fps under `minFps × severeFpsRatio`, or inference over `inferBudgetMs × severeInferRatio`. */
  severeFpsRatio: number;
  severeInferRatio: number;
}

export const DEFAULT_GOVERNOR_PARAMS: GovernorParams = {
  // A110Y: an iPhone infers in ≈ 30 ms — right on the old 30 ms budget, so the slightest warmth
  // stepped it down to minimal in 12 s, and the step-up condition (< 13.5 ms) was out of reach.
  // With the frame lock at every level, 45 ms of inference is painless.
  inferBudgetMs: 45,
  minFps: 24,
  stepDownAfterMs: 3000,
  stepUpAfterMs: 8000,
  locked: false,
  startupWindowMs: 15000,
  severeStepDownAfterMs: 900,
  severeFpsRatio: 0.5,
  severeInferRatio: 2,
};

export class QualityGovernor {
  readonly params: GovernorParams;
  private level: number;
  private overSince = -1;
  private underSince = -1;
  private lastChange = 0;
  private held = false;
  private armedAt = -Infinity;
  private listeners: Array<(level: QualityLevel, reason: string) => void> = [];

  constructor(initialLevel = QualityGovernor.guessInitialLevel(), params: Partial<GovernorParams> = {}) {
    this.level = initialLevel;
    this.params = { ...DEFAULT_GOVERNOR_PARAMS, ...params };
  }

  /** Cheap device heuristics: start lower on modest hardware, the governor adjusts from there. */
  static guessInitialLevel(): number {
    if (typeof navigator === 'undefined') return 0;
    const nav = navigator as Navigator & { deviceMemory?: number };
    const cores = navigator.hardwareConcurrency ?? 4;
    const mem = nav.deviceMemory ?? 4;
    const isApple = /iPhone|iPad|Macintosh/.test(navigator.userAgent);
    // Apple devices start at the top level, as before A110Y: with the 45 ms budget an iPhone
    // (≈ 30 ms of inference) now stays there instead of sliding down to minimal.
    if (isApple) return 0;
    if (cores <= 4 || mem <= 3) return 2;
    if (cores <= 6 || mem <= 4) return 1;
    return 0;
  }

  get current(): QualityLevel {
    return QUALITY_LEVELS[this.level]!;
  }

  get levelIndex(): number {
    return this.level;
  }

  setLevel(index: number, reason = 'manual'): void {
    const clamped = Math.max(0, Math.min(QUALITY_LEVELS.length - 1, index));
    if (clamped === this.level) return;
    this.level = clamped;
    this.lastChange = performance.now();
    this.overSince = -1;
    this.underSince = -1;
    for (const l of this.listeners) l(this.current, reason);
  }

  onChange(listener: (level: QualityLevel, reason: string) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /** Stops judging until `arm()` (engine warm-up: measurements are not representative). */
  hold(): void {
    this.held = true;
    this.overSince = -1;
    this.underSince = -1;
  }

  get isHeld(): boolean {
    return this.held;
  }

  /** Starts judging at `nowMs`, opening the start-up window. */
  arm(nowMs: number): void {
    this.held = false;
    this.armedAt = nowMs;
    this.overSince = -1;
    this.underSince = -1;
  }

  /** Inside the start-up window that follows `arm()`. */
  inStartupWindow(nowMs: number): boolean {
    return !this.held && nowMs - this.armedAt < this.params.startupWindowMs;
  }

  /** Call ~once per second with fresh metrics. */
  update(perf: PerfSnapshot, nowMs: number): void {
    if (this.params.locked || this.held) return;
    const p = this.params;
    const over = perf.inferMs > p.inferBudgetMs || (perf.fps > 0 && perf.fps < p.minFps);
    // Step up once the budget holds with a margin (not only at 45 % of it, which a phone never reaches).
    const under = perf.inferMs < p.inferBudgetMs * 0.7 && perf.fps > p.minFps * 1.3;
    const severe = this.inStartupWindow(nowMs) && (perf.inferMs > p.inferBudgetMs * p.severeInferRatio || (perf.fps > 0 && perf.fps < p.minFps * p.severeFpsRatio));

    if (over) {
      this.underSince = -1;
      if (this.overSince < 0) this.overSince = nowMs;
      if (nowMs - this.overSince > (severe ? p.severeStepDownAfterMs : p.stepDownAfterMs) && this.level < QUALITY_LEVELS.length - 1) {
        this.setLevel(this.level + 1, `over budget: infer ${perf.inferMs.toFixed(0)} ms, ${perf.fps.toFixed(0)} fps`);
      }
    } else if (under) {
      this.overSince = -1;
      if (this.underSince < 0) this.underSince = nowMs;
      if (nowMs - this.underSince > p.stepUpAfterMs && this.level > 0 && nowMs - this.lastChange > p.stepUpAfterMs) {
        this.setLevel(this.level - 1, `headroom: infer ${perf.inferMs.toFixed(0)} ms, ${perf.fps.toFixed(0)} fps`);
      }
    } else {
      this.overSince = -1;
      this.underSince = -1;
    }
  }
}
